import {
  Bell,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Pencil,
  Trophy,
  Video,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import {
  getCourse,
  getPublicAcademyBySlug,
  getStudentCourseSignups,
  getStudentVisibleClasses,
  getTenant,
} from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { getHostnameContext } from '../../utils/hostname';
import { buildClassAccessPath } from '../../utils/classSchedule';
import { StudentSidebar } from './StudentSidebar';

const WEEK_DAYS = [
  { short: 'Sun', key: 'sunday' },
  { short: 'Mon', key: 'monday' },
  { short: 'Tue', key: 'tuesday' },
  { short: 'Wed', key: 'wednesday' },
  { short: 'Thu', key: 'thursday' },
  { short: 'Fri', key: 'friday' },
  { short: 'Sat', key: 'saturday' },
];

function minutesBetween(startTime, endTime) {
  const [startHour = 0, startMinute = 0] = String(startTime || '').split(':').map(Number);
  const [endHour = 0, endMinute = 0] = String(endTime || '').split(':').map(Number);
  const minutes = ((endHour * 60) + endMinute) - ((startHour * 60) + startMinute);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
}

function buildWeeklyActivity(classes) {
  const activity = WEEK_DAYS.map((day) => ({ ...day, hours: 0 }));
  classes.forEach((classItem) => {
    const hours = minutesBetween(classItem.startTime, classItem.endTime) / 60;
    let dayKeys = classItem.recurrence?.daysOfWeek || [];
    if (!dayKeys.length && classItem.scheduledDate) {
      const date = new Date(`${classItem.scheduledDate}T12:00:00`);
      if (!Number.isNaN(date.getTime())) dayKeys = [WEEK_DAYS[date.getDay()].key];
    }
    dayKeys.forEach((dayKey) => {
      const day = activity.find((item) => item.key === String(dayKey).toLowerCase());
      if (day) day.hours += hours;
    });
  });
  return activity;
}

function formatClock(value) {
  const [hoursPart, minutesPart = '00'] = String(value || '').split(':');
  const hours = Number(hoursPart);
  if (!Number.isFinite(hours)) return 'Any time';
  return `${hours % 12 || 12}:${minutesPart}`;
}

function statusIsActive(status) {
  return ['active', 'admitted', 'enrolled', 'approved', 'completed'].includes(String(status || '').toLowerCase());
}

function getProgress(record) {
  const progress = Number(record.progressPercentage ?? record.progress ?? 0);
  return Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0;
}

export function StudentPortalPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [classes, setClasses] = useState([]);
  const [error, setError] = useState('');
  const hostnameContext = useMemo(() => getHostnameContext(), []);

  const displayName = useMemo(() => {
    const fallbackName = String(user?.email || 'Student').split('@')[0];
    return user?.displayName || fallbackName;
  }, [user]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!user?.uid) {
        if (mounted) setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const signups = await getStudentCourseSignups(user.uid).catch((error) => {
          console.warn('Unable to load student course signups.', error);
          return [];
        });
        let scopedSignups = signups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug).catch((error) => {
            console.warn('Unable to load academy details for student portal.', error);
            return null;
          });
          scopedSignups = academy ? signups.filter((signup) => signup.tenantId === academy.id) : [];
        }

        const enriched = await Promise.all(scopedSignups.map(async (signup) => {
          const tenantId = signup.tenantId || signup.ref?.parent?.parent?.id || '';
          const [tenant, course] = await Promise.all([
            tenantId ? getTenant(tenantId).catch((error) => {
              console.warn('Unable to load student academy details.', error);
              return null;
            }) : Promise.resolve(null),
            tenantId && signup.courseId ? getCourse(tenantId, signup.courseId).catch((error) => {
              console.warn('Unable to load student course details.', error);
              return null;
            }) : Promise.resolve(null),
          ]);
          return { ...signup, tenantId, tenant, course };
        }));

        const tenantIds = [...new Set(enriched.map((record) => record.tenantId).filter(Boolean))];
        const classGroups = await Promise.all(tenantIds.map(async (tenantId) => {
          const tenantRecords = enriched.filter((record) => record.tenantId === tenantId);
          const courseIds = new Set(tenantRecords.map((record) => record.courseId).filter(Boolean));
          let tenantClasses = [];
          try {
            tenantClasses = await getStudentVisibleClasses(tenantId);
          } catch (error) {
            console.warn('Unable to load student-visible classes.', error);
          }
          return tenantClasses
            .filter((classItem) => courseIds.has(classItem.courseId))
            .map((classItem) => ({
              ...classItem,
              tenant: tenantRecords[0]?.tenant,
              course: tenantRecords.find((record) => record.courseId === classItem.courseId)?.course,
            }));
        }));

        if (mounted) {
          setRecords(enriched);
          setClasses(classGroups.flat());
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          setError(err?.code === 'permission-denied' ? '' : (err.message || 'Unable to load your student dashboard.'));
          setLoading(false);
        }
      }
    }

    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading student portal..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  const primaryRecord = records[0];
  const academyName = primaryRecord?.tenant?.name || 'Student workspace';
  const firstName = displayName.split(' ')[0];
  const initials = displayName.split(' ').map((part) => part.charAt(0)).join('').slice(0, 2).toUpperCase();
  const today = new Date();
  const dateLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(today);
  const monthLabel = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(today);
  const calendarDays = Array.from({ length: 5 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index - 2);
    return {
      date: date.getDate(),
      day: new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(date),
      active: index === 2,
    };
  });
  const weeklyActivity = buildWeeklyActivity(classes);
  const maxHours = Math.max(1, ...weeklyActivity.map((day) => day.hours));
  const totalHours = weeklyActivity.reduce((sum, day) => sum + day.hours, 0);
  const activeCourses = records.filter((record) => statusIsActive(record.status) && String(record.status || '').toLowerCase() !== 'completed').length;
  const completedCourses = records.filter((record) => String(record.status || '').toLowerCase() === 'completed').length;
  const pendingCourses = Math.max(0, records.length - activeCourses);
  const courseTotal = Math.max(1, records.length);
  const completedPercent = Math.round((completedCourses / courseTotal) * 100);
  const activePercent = Math.round((activeCourses / courseTotal) * 100);
  const pendingPercent = Math.round((pendingCourses / courseTotal) * 100);
  const donutStyle = records.length ? {
    background: `conic-gradient(#ef3655 0 ${pendingPercent}%, #5964dc ${pendingPercent}% ${pendingPercent + activePercent}%, #62c9ee ${pendingPercent + activePercent}% 100%)`,
  } : undefined;
  const progressRecords = records.slice(0, 2);
  const upcomingClasses = classes.slice(0, 2);

  return (
    <main className="student-portal student-dashboard-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-dashboard-shell">
        <section className="student-dashboard-content">
          <header className="student-dashboard-header">
            <div>
              <h1>Welcome Back, {firstName}</h1>
              <p>{dateLabel}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          {error ? <div className="student-dashboard-alert">{error}</div> : null}

          <section className="student-time-section">
            <div className="student-time-chart-area">
              <div className="student-widget-heading">
                <div>
                  <h2>Time Spendings</h2>
                  <strong>{Math.floor(totalHours)}<small>h</small> {Math.round((totalHours % 1) * 60)}<small>m</small></strong>
                </div>
                <span>Weekly <ChevronRight size={14} /></span>
              </div>

              <div className="student-time-chart">
                <div className="student-chart-scale"><span>10</span><span>08</span><span>06</span><span>04</span><span>02</span><span>0</span></div>
                <div className="student-chart-bars">
                  {weeklyActivity.map((day, index) => (
                    <div className={`student-chart-day${index === today.getDay() ? ' is-today' : ''}`} key={day.key}>
                      <div className="student-chart-bar-track">
                        {index === today.getDay() ? <span>{day.hours.toFixed(1)}h<small>Time spent</small></span> : null}
                        <i style={{ height: `${Math.max(8, (day.hours / maxHours) * 100)}%` }} />
                      </div>
                      <b>{day.short}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="student-time-metrics">
              <article>
                <span className="student-metric-icon student-metric-icon--blue"><Clock3 size={23} /></span>
                <div><small>Hours Spent</small><strong>{Math.round(totalHours)}</strong></div>
              </article>
              <article>
                <span className="student-metric-icon student-metric-icon--gold"><Trophy size={23} /></span>
                <div><small>Active Courses</small><strong>{activeCourses}</strong></div>
              </article>
              <article>
                <span className="student-metric-icon student-metric-icon--violet"><CheckCircle2 size={23} /></span>
                <div><small>Completed</small><strong>{completedCourses}</strong></div>
              </article>
            </div>
          </section>

          <section className="student-dashboard-lower">
            <div className="student-progress-widget">
              <h2>Course Progress</h2>
              <div className="student-progress-list">
                {loading ? <p className="student-widget-empty">Loading courses...</p> : progressRecords.length ? progressRecords.map((record) => {
                  const progress = getProgress(record);
                  return (
                    <Link to={`/student/courses/${record.tenantId}/${record.courseId}`} key={record.id}>
                      <span className="student-progress-ring" style={{ '--progress': `${progress * 3.6}deg` }}><b>{progress}%</b></span>
                      <div><strong>{record.course?.name || record.courseName || 'Your course'}</strong><small>{record.intakeLabel || 'Course learning'}</small></div>
                      <ChevronRight size={17} />
                    </Link>
                  );
                }) : <p className="student-widget-empty">Your enrolled courses will appear here.</p>}
              </div>
            </div>

            <div className="student-statistics-widget">
              <h2>Course Statistics</h2>
              <div className="student-statistics-body">
                <div className={`student-course-donut${records.length ? '' : ' is-empty'}`} style={donutStyle}>
                  <div><strong>{records.length}</strong><small>Total Courses</small></div>
                </div>
                <div className="student-statistics-legend">
                  <span><i className="is-red" /><small>Pending</small><strong>{pendingPercent}%</strong></span>
                  <span><i className="is-violet" /><small>Active</small><strong>{activePercent}%</strong></span>
                  <span><i className="is-blue" /><small>Completed</small><strong>{completedPercent}%</strong></span>
                </div>
              </div>
            </div>
          </section>
        </section>

        <aside className="student-profile-rail">
          <div className="student-profile-rail-heading">
            <h2>My Profile</h2>
            <Link to="/student/profile"><Pencil size={16} />Edit</Link>
          </div>

          <div className="student-profile-portrait">
            {user.photoURL ? <img src={user.photoURL} alt={displayName} /> : <strong>{initials}</strong>}
            <i className="shape-one" /><i className="shape-two" /><i className="shape-three" />
          </div>
          <h3>{displayName}</h3>
          <p>{user.email}</p>

          <div className="student-profile-quick-stats">
            <div><strong>{activeCourses}</strong><small>Active</small></div>
            <div><strong>{Math.round(totalHours)}h</strong><small>Avg. Hour</small></div>
            <div><strong>{records.length}</strong><small>Enrolled</small></div>
          </div>

          <div className="student-mini-calendar">
            <header><button type="button" aria-label="Previous week"><ChevronLeft size={16} /></button><h3>{monthLabel}</h3><button type="button" aria-label="Next week"><ChevronRight size={16} /></button></header>
            <div>{calendarDays.map((day) => <span className={day.active ? 'is-active' : ''} key={`${day.day}-${day.date}`}><b>{day.date}</b><small>{day.day}</small></span>)}</div>
          </div>

          <div className="student-upcoming-widget">
            <div className="student-upcoming-heading"><h2>Upcoming Class</h2><Link to="/student/classes">View all</Link></div>
            <div className="student-upcoming-list">
              {loading ? <p className="student-widget-empty">Loading classes...</p> : upcomingClasses.length ? upcomingClasses.map((classItem) => (
                <Link to={buildClassAccessPath(classItem.tenant?.slug || '', classItem.id)} key={`${classItem.tenant?.id}-${classItem.id}`}>
                  <time>{formatClock(classItem.startTime)}<small>{Number(String(classItem.startTime || '').split(':')[0]) >= 12 ? 'PM' : 'AM'}</small></time>
                  <div><strong>{classItem.title || classItem.course?.name || 'Class session'}</strong><small><Video size={12} />{classItem.locationType === 'physical' ? 'On campus' : 'Online class'}</small></div>
                </Link>
              )) : <div className="student-upcoming-empty"><CalendarDays size={19} /><span>No upcoming classes yet.</span></div>}
            </div>
          </div>

          <Link className="student-profile-course-link" to="/student/courses"><BookOpen size={16} />Open my courses<ChevronRight size={16} /></Link>
        </aside>
      </div>
    </main>
  );
}
